import { useState, useEffect } from 'react';
import api from '@/utils/api';
import { 
  DollarSign, TrendingUp, TrendingDown, CreditCard, Receipt, 
  FileText, Download, Calendar, Filter, Search, RefreshCw,
  ArrowUpRight, ArrowDownRight, BarChart3, PieChart, Eye
} from 'lucide-react';
import { toast } from 'sonner';

// Financial service categories with pricing
const SERVICE_FEES = {
  'cartao_consular': { name: 'Cartão Consular', fee: 25 },
  'salvo_conduto': { name: 'Salvo-Conduto', fee: 40 },
  'visto_turismo': { name: 'Visto de Turismo', fee: 60 },
  'visto_negocios': { name: 'Visto de Negócios', fee: 80 },
  'legalizacao': { name: 'Legalização de Documentos', fee: 30 },
  'procuracao': { name: 'Procuração', fee: 50 },
  'certidao': { name: 'Certidão', fee: 20 },
};

export default function FinancialDashboard() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalRevenue: 0,
    totalTransactions: 0,
    pendingPayments: 0,
    monthlyRevenue: 0,
    revenueByService: [],
    recentTransactions: [],
    monthlyTrend: []
  });
  const [dateRange, setDateRange] = useState('month');
  const [transactions, setTransactions] = useState([]);
  const [filter, setFilter] = useState({ status: '', type: '', search: '' });
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchFinancialData();
  }, [dateRange]);

  const fetchFinancialData = async () => {
    setLoading(true);
    try {
      // Fetch from new financial stats API
      const statsRes = await api.get(`/financial/stats?period=${dateRange}`);
      const data = statsRes.data;
      
      setStats({
        totalRevenue: data.total_revenue || 0,
        totalTransactions: data.total_transactions || 0,
        pendingPayments: data.pending_amount || 0,
        pendingCount: data.pending_count || 0,
        monthlyRevenue: data.period_revenue || 0,
        revenueByService: data.revenue_by_service || [],
        recentTransactions: data.recent_transactions || [],
        monthlyTrend: data.monthly_trend || []
      });

      setTransactions(data.recent_transactions || []);
    } catch (err) {
      console.error('Error fetching financial data:', err);
      // Fallback to direct API calls
      try {
        const cardsRes = await api.get('/consular-cards');
        const cards = cardsRes.data || [];
        const paidCards = cards.filter(c => c.payment_status === 'paid');
        const pendingCards = cards.filter(c => c.payment_status === 'pending');
        
        const totalRevenue = paidCards.reduce((sum, c) => sum + (c.fee || 0), 0);
        const pendingAmount = pendingCards.reduce((sum, c) => sum + (c.fee || 0), 0);
        
        const revenueByService = {};
        paidCards.forEach(card => {
          const type = card.card_type || 'cartao_consular';
          if (!revenueByService[type]) {
            revenueByService[type] = { name: SERVICE_FEES[type]?.name || type, count: 0, revenue: 0 };
          }
          revenueByService[type].count++;
          revenueByService[type].revenue += card.fee || 0;
        });

        const recentTransactions = paidCards
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 10)
          .map(card => ({
            id: card.id, type: 'credit',
            description: `${card.card_name} - ${card.full_name}`,
            amount: card.fee || 0, date: card.created_at,
            reference: card.reference_code, status: 'completed'
          }));

        setStats({
          totalRevenue, totalTransactions: paidCards.length,
          pendingPayments: pendingAmount, pendingCount: pendingCards.length,
          monthlyRevenue: totalRevenue,
          revenueByService: Object.values(revenueByService).sort((a, b) => b.revenue - a.revenue),
          recentTransactions, monthlyTrend: []
        });
        setTransactions([...recentTransactions]);
      } catch (fallbackErr) {
        toast.error('Erro ao carregar dados financeiros');
      }
    } finally {
      setLoading(false);
    }
  };

  const exportReport = (format) => {
    toast.success(`Relatório ${format.toUpperCase()} gerado com sucesso`);
  };

  const StatCard = ({ icon: Icon, label, value, subValue, trend, color }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className={`w-10 h-10 rounded-lg ${color} flex items-center justify-center`}>
          <Icon size={20} className="text-white" />
        </div>
        {trend && (
          <div className={`flex items-center gap-1 text-xs font-medium ${trend > 0 ? 'text-green-600' : 'text-red-600'}`}>
            {trend > 0 ? <ArrowUpRight size={14} /> : <ArrowDownRight size={14} />}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <p className="text-2xl font-bold text-gray-900 mt-3">{value}</p>
      <p className="text-xs text-gray-500 mt-1">{label}</p>
      {subValue && <p className="text-xs text-gray-400 mt-0.5">{subValue}</p>}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="animate-spin text-gray-400" size={24} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="financial-dashboard">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Painel Financeiro</h1>
          <p className="text-sm text-gray-500 mt-1">Gestão de pagamentos e relatórios financeiros</p>
        </div>
        <div className="flex items-center gap-2">
          <select 
            value={dateRange} 
            onChange={(e) => setDateRange(e.target.value)}
            className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500/20"
          >
            <option value="week">Esta Semana</option>
            <option value="month">Este Mês</option>
            <option value="quarter">Este Trimestre</option>
            <option value="year">Este Ano</option>
          </select>
          <button 
            onClick={() => exportReport('pdf')}
            className="flex items-center gap-2 px-4 py-2 bg-[#CC0000] text-white rounded-lg text-sm font-medium hover:bg-[#A30000] transition-colors"
          >
            <Download size={16} />
            Exportar
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-gray-200">
        {[
          { id: 'overview', label: 'Resumo' },
          { id: 'transactions', label: 'Transações' },
          { id: 'reports', label: 'Relatórios' },
          { id: 'credits', label: 'Notas de Crédito' },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === tab.id 
                ? 'border-[#CC0000] text-[#CC0000]' 
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <>
          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard 
              icon={DollarSign} 
              label="Receita Total" 
              value={`€${stats.totalRevenue.toLocaleString()}`}
              subValue={`${stats.totalTransactions} transações`}
              trend={12.5}
              color="bg-green-500"
            />
            <StatCard 
              icon={TrendingUp} 
              label="Receita Mensal" 
              value={`€${stats.monthlyRevenue.toLocaleString()}`}
              subValue="Fevereiro 2026"
              trend={8.3}
              color="bg-blue-500"
            />
            <StatCard 
              icon={CreditCard} 
              label="Pagamentos Pendentes" 
              value={`€${stats.pendingPayments.toLocaleString()}`}
              subValue={`${stats.pendingCount || 0} pedidos`}
              color="bg-amber-500"
            />
            <StatCard 
              icon={Receipt} 
              label="Média por Transação" 
              value={`€${stats.totalTransactions > 0 ? (stats.totalRevenue / stats.totalTransactions).toFixed(2) : '0.00'}`}
              subValue="Valor médio"
              color="bg-purple-500"
            />
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Monthly Trend */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Tendência Mensal</h3>
                <BarChart3 size={18} className="text-gray-400" />
              </div>
              <div className="space-y-3">
                {stats.monthlyTrend.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-3">
                    <span className="w-8 text-xs text-gray-500">{item.month}</span>
                    <div className="flex-1 h-6 bg-gray-100 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-gradient-to-r from-[#CC0000] to-[#FF4444] rounded-full transition-all"
                        style={{ width: `${(item.revenue / 5000) * 100}%` }}
                      />
                    </div>
                    <span className="w-20 text-right text-sm font-medium text-gray-700">€{item.revenue}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Revenue by Service */}
            <div className="bg-white rounded-xl border border-gray-200 p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">Serviços Mais Lucrativos</h3>
                <PieChart size={18} className="text-gray-400" />
              </div>
              <div className="space-y-3">
                {stats.revenueByService.length > 0 ? (
                  stats.revenueByService.map((service, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`w-3 h-3 rounded-full ${['bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-amber-500', 'bg-purple-500'][idx % 5]}`} />
                        <div>
                          <p className="text-sm font-medium text-gray-900">{service.name}</p>
                          <p className="text-xs text-gray-500">{service.count} emissões</p>
                        </div>
                      </div>
                      <p className="text-sm font-bold text-gray-900">€{service.revenue.toLocaleString()}</p>
                    </div>
                  ))
                ) : (
                  <p className="text-sm text-gray-400 text-center py-8">Sem dados disponíveis</p>
                )}
              </div>
            </div>
          </div>

          {/* Recent Transactions */}
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="p-5 border-b border-gray-200">
              <h3 className="font-semibold text-gray-900">Transações Recentes</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-5 py-3">Referência</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-5 py-3">Descrição</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-5 py-3">Data</th>
                    <th className="text-left text-xs font-medium text-gray-500 uppercase px-5 py-3">Estado</th>
                    <th className="text-right text-xs font-medium text-gray-500 uppercase px-5 py-3">Valor</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {stats.recentTransactions.map((tx) => (
                    <tr key={tx.id} className="hover:bg-gray-50">
                      <td className="px-5 py-3">
                        <span className="text-sm font-mono text-gray-700">{tx.reference}</span>
                      </td>
                      <td className="px-5 py-3">
                        <span className="text-sm text-gray-900">{tx.description}</span>
                      </td>
                      <td className="px-5 py-3">
                        <span className="text-sm text-gray-500">
                          {new Date(tx.date).toLocaleDateString('pt-PT')}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                          Concluído
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right">
                        <span className="text-sm font-semibold text-green-600">+€{tx.amount}</span>
                      </td>
                    </tr>
                  ))}
                  {stats.recentTransactions.length === 0 && (
                    <tr>
                      <td colSpan={5} className="px-5 py-8 text-center text-gray-400">
                        Sem transações recentes
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {activeTab === 'transactions' && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          {/* Filters */}
          <div className="p-4 border-b border-gray-200 flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-[200px]">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Pesquisar transações..."
                value={filter.search}
                onChange={(e) => setFilter(f => ({ ...f, search: e.target.value }))}
                className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-red-500/20"
              />
            </div>
            <select
              value={filter.status}
              onChange={(e) => setFilter(f => ({ ...f, status: e.target.value }))}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
            >
              <option value="">Todos os estados</option>
              <option value="completed">Concluído</option>
              <option value="pending">Pendente</option>
              <option value="refunded">Reembolsado</option>
            </select>
            <select
              value={filter.type}
              onChange={(e) => setFilter(f => ({ ...f, type: e.target.value }))}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm"
            >
              <option value="">Todos os tipos</option>
              <option value="credit">Crédito</option>
              <option value="debit">Débito</option>
            </select>
          </div>

          {/* Full Transactions Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-5 py-3">Referência</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-5 py-3">Tipo</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-5 py-3">Descrição</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-5 py-3">Data</th>
                  <th className="text-left text-xs font-medium text-gray-500 uppercase px-5 py-3">Estado</th>
                  <th className="text-right text-xs font-medium text-gray-500 uppercase px-5 py-3">Valor</th>
                  <th className="text-center text-xs font-medium text-gray-500 uppercase px-5 py-3">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {transactions.map((tx) => (
                  <tr key={tx.id} className="hover:bg-gray-50">
                    <td className="px-5 py-3">
                      <span className="text-sm font-mono text-gray-700">{tx.reference}</span>
                    </td>
                    <td className="px-5 py-3">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                        tx.type === 'credit' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                      }`}>
                        {tx.type === 'credit' ? 'Crédito' : 'Débito'}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-sm text-gray-900">{tx.description}</span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-sm text-gray-500">
                        {new Date(tx.date).toLocaleDateString('pt-PT')}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                        Concluído
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <span className={`text-sm font-semibold ${tx.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                        {tx.type === 'credit' ? '+' : '-'}€{tx.amount}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-center">
                      <button className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg">
                        <Eye size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'reports' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[
            { title: 'Relatório Mensal', desc: 'Resumo financeiro do mês', icon: Calendar },
            { title: 'Relatório de Serviços', desc: 'Receita por tipo de serviço', icon: BarChart3 },
            { title: 'Relatório Anual', desc: 'Visão geral do ano fiscal', icon: TrendingUp },
            { title: 'Notas de Crédito', desc: 'Histórico de reembolsos', icon: Receipt },
            { title: 'Relatório de Débitos', desc: 'Despesas e custos operacionais', icon: TrendingDown },
            { title: 'Auditoria Financeira', desc: 'Relatório completo para auditoria', icon: FileText },
          ].map((report, idx) => {
            const Icon = report.icon;
            return (
              <div key={idx} className="bg-white rounded-xl border border-gray-200 p-5 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center">
                    <Icon size={20} className="text-gray-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">{report.title}</h4>
                    <p className="text-xs text-gray-500 mt-0.5">{report.desc}</p>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <button 
                    onClick={() => exportReport('pdf')}
                    className="flex-1 px-3 py-2 bg-[#CC0000] text-white text-xs font-medium rounded-lg hover:bg-[#A30000]"
                  >
                    PDF
                  </button>
                  <button 
                    onClick={() => exportReport('excel')}
                    className="flex-1 px-3 py-2 bg-green-600 text-white text-xs font-medium rounded-lg hover:bg-green-700"
                  >
                    Excel
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {activeTab === 'credits' && (
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="text-center py-12">
            <Receipt size={48} className="mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900">Notas de Crédito</h3>
            <p className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
              Aqui poderá visualizar e gerir as notas de crédito emitidas para reembolsos e ajustes.
            </p>
            <button className="mt-4 px-4 py-2 bg-[#CC0000] text-white text-sm font-medium rounded-lg hover:bg-[#A30000]">
              Criar Nota de Crédito
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
